import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TimelineItem } from '@/types/item';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export interface AssignedUserProfile {
  id: string;
  full_name: string;
  avatar_url?: string | null;
}

interface MilestoneWithAssignee {
  assigned_user_id?: string | null;
}

/**
 * Hook to fetch profiles for all assigned users in timeline items and milestones
 * Used for multi-project timeline report exports where useProjectTeamMembers won't work
 */
export const useAssignedUserProfiles = (
  items: TimelineItem[], 
  milestonesByItem?: Record<string, MilestoneWithAssignee[]>,
  enabled = true
) => {
  // Extract unique assigned_user_ids from items and milestones
  const assignedUserIds = useMemo(() => {
    const ids = new Set<string>();
    
    // From items
    items.forEach(item => {
      if (item.assigned_user_id) ids.add(item.assigned_user_id);
    });
    
    // From milestones
    if (milestonesByItem) {
      Object.values(milestonesByItem).forEach(milestones => {
        milestones.forEach(m => {
          if (m.assigned_user_id) ids.add(m.assigned_user_id);
        });
      });
    }
    
    return Array.from(ids);
  }, [items, milestonesByItem]);

  return useQuery({
    queryKey: ['assigned-user-profiles', assignedUserIds],
    queryFn: async (): Promise<AssignedUserProfile[]> => {
      if (assignedUserIds.length === 0) return [];
      
      const data = await chunkedInQuerySimple<AssignedUserProfile>(
        supabase,
        'profiles' as any,
        'id',
        assignedUserIds,
        'id, full_name, avatar_url'
      );
      
      return data || [];
    },
    enabled: enabled && assignedUserIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
